'use client';

import { Children, ReactNode } from 'react';
import { isMobile } from 'react-device-detect';
import CheckHydration from '@utils/CheckHydration';
import { ProductCardData } from '@utils/productDto';
import { Professional } from 'app/types/clinic';
import Carousel from 'designSystem/Carousel/Carousel';
import StaticSlides from 'designSystem/Carousel/StaticSlides';
import { isEmpty } from 'lodash';

import ProfessionalCard from '../common/ProfessionalCard';
import ProductCard from './ProductCard';

export type FullWidthCarouselProps = {
  type?: 'products' | 'professionals';
  visibleSlides?: number | null;
  className?: string;
  /** Tarjetas de producto: pasar `toProductCardDataList(...)`, no el Product entero. */
  items?: ProductCardData[] | Professional[] | null;
  hasDots?: boolean;
  hasControls?: boolean;
  isPlaying?: boolean;
  disableLeftMargin?: boolean;
  children?: ReactNode;
  dragEnabled?: boolean;
  linedDots?: boolean;
  customLeftMargin?: number | null;
  itemWidth?: number;
  onClick?: (...args: any[]) => void;
  withoutLink?: boolean;
  hidePrice?: boolean;
  showMoreButton?: boolean;
  /**
   * Ajustes que solo aplican en móvil. Se resuelven en cliente (el carrusel
   * solo se pinta tras hidratar), sin leer el user-agent en el servidor.
   */
  mobile?: Partial<
    Pick<
      FullWidthCarouselProps,
      | 'visibleSlides'
      | 'hasDots'
      | 'hasControls'
      | 'dragEnabled'
      | 'itemWidth'
      | 'className'
    >
  >;
};

export default function FullWidthCarousel(props: FullWidthCarouselProps) {
  const { mobile, ...baseProps } = props;
  const resolvedProps: Omit<FullWidthCarouselProps, 'mobile'> =
    isMobile && mobile ? { ...baseProps, ...mobile } : baseProps;

  const {
    type = 'products',
    visibleSlides,
    className,
    items,
    hasDots = false,
    hasControls = true,
    isPlaying = false,
    disableLeftMargin = false,
    children,
    dragEnabled = true,
    linedDots = false,
    customLeftMargin = null,
    itemWidth = 304, //default productCard width
    onClick,
    withoutLink = false,
    hidePrice = false,
    showMoreButton = false,
  } = resolvedProps;

  const randomId = Math.random().toString().slice(2, 5);

  const CONTAINER_WIDTH = 1152;
  const CONTAINER_PADDING = 16;
  let firstItemLeftMargin = 16;
  let slidesToShow = visibleSlides ? visibleSlides : 1.5;

  if (typeof window !== 'undefined') {
    if (document && document.body.clientWidth > CONTAINER_WIDTH) {
      firstItemLeftMargin =
        (document.body.clientWidth - CONTAINER_WIDTH) / 2 + CONTAINER_PADDING;
    }

    if (!visibleSlides) {
      slidesToShow =
        (document.body.clientWidth - firstItemLeftMargin) / itemWidth;
    }
  }

  if (isEmpty(items) && !children) {
    return <></>;
  }

  // Array plano de tarjetas: el carrusel y su versión estática las necesitan
  // sueltas (Children.toArray), no dentro de un fragmento.
  const productSlides =
    type === 'products' && items
      ? (items as any[])
          .filter((product: ProductCardData | any) => product.visibility)
          .map((product: ProductCardData | any) => (
            <ProductCard
              withoutLink={withoutLink}
              key={product.id}
              product={product}
              className="h-full flex flex-col mr-6"
              onClick={onClick ? () => onClick(product) : undefined}
              hidePrice={hidePrice}
              showMoreButton={showMoreButton}
            />
          ))
      : [];

  const professionalSlides =
    type === 'professionals' && items
      ? (items as any[])
          .filter((professional: Professional | any) => professional.urlPhoto !== '')
          .map((professional: Professional | any) => (
            <ProfessionalCard
              key={professional.name}
              professional={professional}
              className="h-full flex flex-col mr-10"
            />
          ))
      : [];

  const slides = [
    ...productSlides,
    ...professionalSlides,
    ...Children.toArray(children),
  ];

  return (
    <CheckHydration
      fallback={
        <StaticSlides
          visibleSlides={baseProps.visibleSlides ?? 3.8}
          mobileVisibleSlides={mobile?.visibleSlides ?? 1.2}
          className={baseProps.className}
          isFullWidth={!baseProps.disableLeftMargin}
        >
          {slides}
        </StaticSlides>
      }
    >
      <style>
        {`
          #productCarousel${randomId} [aria-label="slider"] {
            padding-left: ${
              customLeftMargin
                ? customLeftMargin
                : disableLeftMargin
                  ? '0'
                  : firstItemLeftMargin
            }px !important;
          }
        `}
      </style>
      <Carousel
        id={`productCarousel${randomId}`}
        hasDots={hasDots}
        hasControls={hasControls && !isPlaying}
        className={`relative ${className}`}
        isIntrinsicHeight
        visibleSlides={slidesToShow}
        isPlaying={isPlaying}
        isFullWidth
        dragEnabled={dragEnabled}
        linedDots={linedDots}
      >
        {slides}
      </Carousel>
    </CheckHydration>
  );
}
